import { Badge, Group, Stack, Text, rem } from '@mantine/core';
import type { OrderPricing } from '../../types';
import {
  FREE_FOR_MEMBERS_LABEL,
  MEMBER_PRICE_UPGRADE_HINT,
  OrderPricingAudiences,
} from '../../constants/orderPricing';
import { brandColors, semanticColors } from '../../config/theme';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { formatMembersPayLabel } from '../../utils/formatMembersPayLabel';
import { formatUsd } from '../../utils/formatUsd';

/** Compact tile callout size — matches CatalogCompactTile / e-card tiles. */
const COMPACT_MEMBER_PRICE_FONT_SIZE = rem(10);

export interface PriceDisplayProps {
  /** Tier-aware pricing from the API. */
  pricing: OrderPricing;
  /**
   * When true (catalog tiles / pickers), show list + compact Members pay /
   * Free for members on one row (no upgrade hint).
   * When false (order/review modals), show full list/member comparison.
   */
  compact?: boolean;
  /** Mantine Text size for the dominant price. */
  size?: string;
  /** When true, show sale-style strikethrough for members only. */
  showMemberComparison?: boolean;
  /** Horizontal alignment for PDP / hero layouts (default left). */
  align?: 'left' | 'right';
  /**
   * When true, never show the compact "Members pay" / "Free for members" callout.
   * Used by the ChatGPT catalog widget (OpenAI policy: no plan promotion).
   */
  hideMemberCallout?: boolean;
}

/**
 * Formats the primary amount for the current viewer.
 * Zero member prices render as "Free" instead of "$0.00".
 */
function formatViewerPriceUsd(amount: number): string {
  return amount <= 0 ? 'Free' : formatUsd(amount);
}

/**
 * Consolidated tier-aware price display (PBI #1186 / #1187).
 *
 * Guests and free-plan users see list price + a "Members pay" / "Free for
 * members" callout. Paid subscribers never see that upsell — they see struck
 * list + member price (even when a public catalog payload still says
 * audience=public).
 */
export function PriceDisplay({
  pricing,
  compact = false,
  size = 'sm',
  showMemberComparison = true,
  align = 'left',
  hideMemberCallout = false,
}: PriceDisplayProps) {
  const { isSubscribed } = useSubscription();
  const isRightAligned = align === 'right';

  // Prefer live subscription state: public browse endpoints often stamp
  // audience=public even for signed-in members, which would otherwise keep
  // showing the "Members pay" upsell to people who already subscribe.
  const isMemberViewer =
    isSubscribed || pricing.audience === OrderPricingAudiences.Member;

  const memberDisplayUsd = isSubscribed && pricing.audience !== OrderPricingAudiences.Member
    ? pricing.memberUsd
    : pricing.effectiveUsd;

  const membersPayLabel = formatMembersPayLabel(pricing.listUsd, pricing.memberUsd);
  const showMembersPayCallout = !hideMemberCallout && !isMemberViewer && membersPayLabel != null;

  if (compact) {
    const primaryUsd = isMemberViewer ? memberDisplayUsd : pricing.effectiveUsd;

    return (
      <Group
        gap={4}
        wrap="nowrap"
        justify="space-between"
        align="baseline"
        style={{ width: '100%' }}
      >
        <Text size={size} fw={500} c={semanticColors.text.secondary} style={{ flexShrink: 0 }} component="span">
          {formatViewerPriceUsd(primaryUsd)}
        </Text>
        {showMembersPayCallout ? (
          <Text
            fz={COMPACT_MEMBER_PRICE_FONT_SIZE}
            fw={600}
            c={brandColors.success}
            ta="right"
            lineClamp={1}
            aria-label={membersPayLabel}
            style={{ flex: '1 1 auto', minWidth: 0 }}
            component="span"
          >
            {membersPayLabel}
          </Text>
        ) : null}
      </Group>
    );
  }

  if (
    isMemberViewer
    && showMemberComparison
    && pricing.listUsd > memberDisplayUsd
  ) {
    // Badge before amounts so the charged price stays the rightmost cell and
    // lines up with Gift card / Total on confirmation and Review summaries.
    return (
      <Group gap={6} wrap="nowrap" align="baseline" justify={isRightAligned ? 'flex-end' : undefined}>
        <Badge size="sm" variant="light" color={brandColors.success}>
          {memberDisplayUsd <= 0 ? FREE_FOR_MEMBERS_LABEL : 'Member price'}
        </Badge>
        <Text
          size={size}
          c="dimmed"
          td="line-through"
          aria-label={`List price ${formatUsd(pricing.listUsd)}`}
          component="span"
        >
          {formatUsd(pricing.listUsd)}
        </Text>
        <Text
          size={size}
          fw={600}
          aria-label={`Member price ${formatViewerPriceUsd(memberDisplayUsd)}`}
          component="span"
        >
          {formatViewerPriceUsd(memberDisplayUsd)}
        </Text>
      </Group>
    );
  }

  return (
    <Stack gap={4} align={isRightAligned ? 'flex-end' : undefined}>
      <Group gap="sm" wrap="wrap" align="center" justify={isRightAligned ? 'flex-end' : undefined}>
        <Text size={size} fw={600} component="span">
          {formatViewerPriceUsd(pricing.effectiveUsd)}
        </Text>
        {showMembersPayCallout && membersPayLabel && (
          <Badge
            variant="outline"
            color={brandColors.success}
            radius="sm"
            tt="none"
            h="auto"
            px="sm"
            py={4}
            aria-label={membersPayLabel}
            styles={{
              // Match the adjacent list-price Text size (e.g. size="lg" in preview modals).
              label: {
                fontSize: `var(--mantine-font-size-${size})`,
                lineHeight: `var(--mantine-line-height-${size})`,
                fontWeight: 700,
                textTransform: 'none',
              },
            }}
          >
            {membersPayLabel}
          </Badge>
        )}
      </Group>
      {showMembersPayCallout && (
        <Text size="sm" c={semanticColors.text.secondary} ta={isRightAligned ? 'right' : undefined}>
          {MEMBER_PRICE_UPGRADE_HINT}
        </Text>
      )}
    </Stack>
  );
}
